import React, { useEffect, useRef, useState } from "react";

/* Scroll-triggered reveal.

   IntersectionObserver plus a CSS transition rather than an animation library:
   about 60 lines, nothing extra shipped to the browser, and the intersection
   work happens off the main thread instead of in a scroll handler.

   Three things it has to get right, in order of how badly they break the page:

   1. Content must never be trapped invisible. The hidden class is applied by
      this component after mount, so no-JS, no-IntersectionObserver and a
      crawler all get plain visible content. Hiding in CSS and revealing with
      JS is the version of this that silently blanks a landing page.
   2. It fires once. An element that fades out again on the way back up turns
      scrolling into a fight.
   3. It honours prefers-reduced-motion. Handled in CSS so it cannot be missed.

   On the scroll backstop below: IntersectionObserver only calls back when a
   threshold is *crossed*. Jump straight to an anchor and an element between the
   old and new scroll positions goes from below the viewport (ratio 0) to above
   it (ratio 0) with no frame in between, so no threshold is crossed, no
   callback arrives, and it stays invisible permanently. The nav on this page
   links to #how-it-works, so that is a live path, not a hypothetical. The
   observer handles the normal case cheaply; a rAF-throttled scroll listener
   catches the skipped one. Both are torn down the moment the element is shown.

   `i` staggers a group: each item waits i × 70ms, which reads as one movement
   rather than several. Keep groups small; a stagger over ten items ends with
   the reader waiting on the last one. */

const STAGGER_MS = 70;

/* The observation half, on its own, for anything that needs to *do* something
   when it scrolls into view rather than just fade in — the Sift Score card
   counts itself up, which no CSS class can express. Same guarantees: fires
   once, survives a skipped scroll, and reports true immediately when there is
   no IntersectionObserver, so behaviour never depends on the animation running.

   `armed` is false for content that was already on screen at mount. Callers use
   it to tell "reveal it now" from "it was always here", so an above-the-fold
   card renders finished instead of playing to nobody. */
export function useInView() {
  const ref = useRef(null);
  const [armed, setArmed] = useState(false);
  const [seen, setSeen] = useState(false);

  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") { setSeen(true); return; }

    let io = null;
    let onScroll = null;
    let raf2 = 0;

    const teardown = () => {
      io?.disconnect();
      if (onScroll) window.removeEventListener("scroll", onScroll);
      io = null; onScroll = null;
    };
    const show = () => { setSeen(true); teardown(); };

    /* Measure after layout has settled, not at mount. The hero fetches its card
       and its count asynchronously; measuring before those land put everything
       below them a screen too high, so every element looked above-the-fold and
       nothing ever animated. */
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        const el = ref.current;
        if (!el) return;
        // Already on screen: show it without animating, or the first paint is a
        // page fading in from nothing.
        if (el.getBoundingClientRect().top < window.innerHeight) { setSeen(true); return; }

        setArmed(true);

        io = new IntersectionObserver(
          ([entry]) => { if (entry.isIntersecting) show(); },
          // A little under a fifth visible, and 12% short of the fold, so it has
          // finished moving by the time it is comfortably in view.
          { threshold: 0.15, rootMargin: "0px 0px -12% 0px" }
        );
        io.observe(el);

        let ticking = false;
        onScroll = () => {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(() => {
            ticking = false;
            const node = ref.current;
            if (node && node.getBoundingClientRect().top < window.innerHeight) show();
          });
        };
        window.addEventListener("scroll", onScroll, { passive: true });
      });
    });

    return () => { cancelAnimationFrame(raf1); cancelAnimationFrame(raf2); teardown(); };
  }, []);

  return { ref, armed, seen };
}

export default function Reveal({ as: Tag = "div", i = 0, className = "", children, ...rest }) {
  const { ref, armed, seen } = useInView();

  const cls = [className, armed ? "reveal" : "", armed && seen ? "is-in" : ""]
    .filter(Boolean).join(" ");

  return (
    <Tag
      ref={ref}
      className={cls}
      style={armed && !seen ? { transitionDelay: `${i * STAGGER_MS}ms` } : undefined}
      {...rest}
    >
      {children}
    </Tag>
  );
}

/* Injected once by whichever page uses Reveal, rather than a <style> per
   instance, so a row of five cards does not mount five copies. */
export const REVEAL_CSS = `
.reveal { opacity:0; transform:translateY(16px); will-change:opacity, transform;
  transition:opacity .5s cubic-bezier(.22,.61,.36,1), transform .5s cubic-bezier(.22,.61,.36,1); }
.reveal.is-in { opacity:1; transform:none; }
@media (prefers-reduced-motion: reduce) {
  .reveal, .reveal.is-in { opacity:1; transform:none; transition:none; }
}
`;
